import { SxProps, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

export const pageBoxSx: SxProps<Theme> = {
	pt: 4,
	pr: 4,
	pb: 4,
	pl: '24px',
	flex: 1,
	width: '100%',
	boxSizing: 'border-box',
	backgroundColor: '#F9FAFB',
	display: 'flex',
	flexDirection: 'column',
	// ensure the background fills at least the viewport under the top AppBar (56px)
	// but allow the container to grow taller when page content is longer
	minHeight: 'calc(100vh - 56px)'
};

export const titleRowSx: SxProps<Theme> = { display: 'flex', alignItems: 'center', gap: 2 };

// Header row: single-line layout containing title/tagline on left and Create button on right
export const headerRowSx: SxProps<Theme> = {
	height: '51.5px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	pt: '24px',
	pl: 0,
	pr: 0,
	opacity: 1,
};

export const leftTextSx: SxProps<Theme> = {
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
};

export const createButtonWrapperSx: SxProps<Theme> = {};

export const controlsRowSx: SxProps<Theme> = {
	display: 'flex',
	gap: '20px',
	alignItems: 'center',
	height: '35px',
	mt: '24px',
	pl: 0,
	opacity: 1,
};

export const controlFormSx: SxProps<Theme> = {
	height: '35px',
	display: 'flex',
	alignItems: 'center',
};

export const selectSx: SxProps<Theme> = {
	height: '36px',
	color: '#6B7280',
	width: '100%',
	boxSizing: 'border-box',
	fontFamily: 'Inter, Arial, sans-serif',
	fontWeight: 400,
	fontStyle: 'normal',
	fontSize: '13px',
	lineHeight: '19.5px',
	letterSpacing: '-0.08px',
	'& .MuiSelect-select': {
		display: 'flex',
		alignItems: 'center',
		height: '36px',
		color: '#6B7280',
	},
};

export const menuItemSx: SxProps<Theme> = {
	fontFamily: 'Inter, Arial, sans-serif',
	fontWeight: 400,
	fontStyle: 'normal',
	fontSize: '13px',
	lineHeight: '19.5px',
	letterSpacing: '-0.08px',
};

export const allStatusSx: SxProps<Theme> = {

	width: '169px',
	height: '36px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	transform: 'rotate(0deg)',
	opacity: 1,
	pt: '8px',
	pb: '8px',
};

// Combined style object for the "All Statuses" FormControl to satisfy TypeScript's
// expectation of a single style object for the `sx` prop on some MUI components.
export const allStatusCombinedSx: SystemStyleObject<Theme> = {
	...(controlFormSx as SystemStyleObject<Theme>),
	...(allStatusSx as SystemStyleObject<Theme>),
};

// Shared wrapper styles for both dropdown FormControls so they align consistently.
export const selectFormSx: SystemStyleObject<Theme> = {
	height: '36px',
	display: 'flex',
	alignItems: 'center',
	width: '169px',
	justifyContent: 'space-between',
	pt: '8px',
	pr: '15px',
	pb: '8px',
	pl: '15px',
};

export const pageTitleSx: SxProps<Theme> = {
	fontFamily: 'Inter, Arial, sans-serif',
	fontWeight: 700,
	fontStyle: 'normal',
	fontSize: '20px',
	lineHeight: '30px',
	letterSpacing: '-0.45px',
};

export const taglineSx: SxProps<Theme> = {
	fontFamily: 'Inter, Arial, sans-serif',
	fontWeight: 400,
	fontStyle: 'normal',
	fontSize: '13px',
	lineHeight: '19.5px',
	letterSpacing: '-0.08px',
	color: '#6B7280',
};

// Node name main text style
export const nodeNameSx: SxProps<Theme> = {
	fontFamily: 'Inter, Arial, sans-serif',
	fontWeight: 500,
	fontStyle: 'normal',
	fontSize: '13px',
	lineHeight: '19.5px',
	letterSpacing: '-0.08px',
};

// Node subtext (address) style
export const nodeSubTextSx: SxProps<Theme> = {
	fontFamily: 'Inter, Arial, sans-serif',
	fontWeight: 400,
	fontStyle: 'normal',
	fontSize: '11px',
	lineHeight: '16.5px',
	letterSpacing: '0.06px',
	color: '#9CA3AF',
};

// City text style
export const nodeCitySx: SxProps<Theme> = {
	fontFamily: 'Inter, Arial, sans-serif',
	fontWeight: 400,
	fontStyle: 'normal',
	fontSize: '13px',
	lineHeight: '19.5px',
	letterSpacing: '-0.08px',
	color: '#374151',
};

// Table header text style
export const tableHeaderTextSx: SxProps<Theme> = {
	fontFamily: 'Inter, Arial, sans-serif',
	fontWeight: 600,
	fontStyle: 'normal',
	fontSize: '11px',
	lineHeight: '16.5px',
	letterSpacing: '0.06px',
};

export const nodeDetailsSx: SxProps<Theme> = {
	borderRadius: '14px',
	border: '1px solid rgba(0,0,0,0.06)',
	opacity: 1,
	// allow the table to size to its content; when it grows past maxHeight it will scroll
	display: 'block',
	// use a responsive width and a fixed viewport-based height so the surrounding layout (sidebar/admin area)
	// remains visible; the table body will scroll internally when content overflows
	width: '100%',
	maxWidth: '1100px',
	alignSelf: 'flex-start',
	// use maxHeight so the DataGrid's internal virtual scroller handles scrolling
	maxHeight: 'calc(100vh - 260px)',
	// let the grid's virtualScroller manage overflow (required for sticky headers)
	overflowY: 'auto',
};

export const createButtonSx: SxProps<Theme> = {
	width: '123px',
	height: '36px',
	borderRadius: '10px',
	backgroundColor: '#39A9B1',
	color: '#FFFFFF',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '8px',
	pt: '8px',
	pr: '12px',
	pb: '8px',
	pl: '12px',
	opacity: 1,
	textTransform: 'none',
	'&:hover': { backgroundColor: '#39A9B1' },
};

// Tag styles for node status
export const activeTagSx: SxProps<Theme> = {
	width: '65.3671875px',
	height: '24.5px',
	borderRadius: '16777200px',
	backgroundColor: '#ECFDF5',
	color: '#064E3B',
	fontFamily: 'Inter, Arial, sans-serif',
	fontWeight: 500,
	fontStyle: 'normal',
	fontSize: '11px',
	lineHeight: '16.5px',
	letterSpacing: '0.06px',
	opacity: 1,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '8px',
	px: 1,
	position: 'relative',
};

export const inactiveTagSx: SxProps<Theme> = {
	width: '74.109375px',
	height: '24.5px',
	borderRadius: '16777200px',
	backgroundColor: '#F3F4F6',
	color: '#374151',
	fontFamily: 'Inter, Arial, sans-serif',
	fontWeight: 500,
	fontStyle: 'normal',
	fontSize: '11px',
	lineHeight: '16.5px',
	letterSpacing: '0.06px',
	opacity: 1,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '8px',
	px: 1,
	position: 'relative',
};

// Exact dot colors used inside the status tags. Exported so other pages can reuse them
export const activeDotColor = '#60AF97';
export const inactiveDotColor = '#9CA3AF';

export const tableHeadSx: SxProps<Theme> = {
	backgroundColor: '#F9FAFB',
	height: '40px',
	minHeight: '40px',
	maxHeight: '40px',
	borderBottom: '1px solid rgba(0,0,0,0.06)',
};

// Power icon size used inside the action button
export const powerIconSx: SxProps<Theme> = {
	width: '14px',
	height: '14px',
};

// Checkbox styling: size, color, rotation, opacity, and spacing
export const checkboxSx: SxProps<Theme> = {
	p: 0,
	m: 0,
	mr: '8px',
	// target the internal SVG icon
	'& .MuiSvgIcon-root': {
		width: '18px',
		height: '18px',
		transform: 'rotate(0deg)',
		opacity: 1,
		color: '#39A9B1',
	},
	// ensure checked state uses the same colour
	'&.Mui-checked': {
		color: '#39A9B1',
	},
};

// Label text for the "Below minimum guard count" checkbox
export const belowMinLabelSx: SxProps<Theme> = {
	fontFamily: 'Inter, Arial, sans-serif',
	fontWeight: 400,
	fontStyle: 'normal',
	fontSize: '13px',
	lineHeight: '19.5px',
	letterSpacing: '-0.08px',
	color: '#111827',
};